import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Bluetooth, Edit, EyeOff, History, Info, Plus, Power, RotateCw, Save, ShieldAlert, Trash2, X } from "lucide-react";
import CompactSelect from "../components/CompactSelect";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import SensorBleSetupModal from "../components/SensorBleSetupModal";
import EditFloatingActions from "../components/EditFloatingActions";
import {
    claimClientSensor,
    createClientSensorShare,
    deleteClientSensorShare,
    detachClientSensor,
    fetchClientSensorEvents,
    listClientSensorShares,
    listClientSensors,
    updateClientSensor,
} from "../services/sensors";
import { pushAndroidBackHandler } from "../utils/androidBack";
import {
    DEFAULT_SENSOR_THRESHOLDS,
    DEFAULT_SENSOR_DEVICE_SETTINGS,
    deviceSettingsFromSensorConfig,
    mergeDeviceSettingsIntoConfig,
    mergeThresholdsIntoConfig,
    mergeTimezoneIntoConfig,
    resolveSensorChannel,
    thresholdsFromSensorConfig,
} from "../utils/sensorThresholds";
import {
    DEFAULT_SENSOR_TIMEZONE,
    SENSOR_TIMEZONE_OPTIONS,
    formatSensorDateTime,
    timezoneFromSensorConfig,
} from "../utils/sensorDateTime";
import "../styles/sensors.css";

const emptyClaimForm = {
    device_uid: "",
    pairing_code: "",
    display_name: "",
    descriptions: "",
};

const emptyEditForm = {
    display_name: "",
    descriptions: "",
    notify_client: true,
    is_active: true,
    thresholds: { ...DEFAULT_SENSOR_THRESHOLDS },
    timezone: DEFAULT_SENSOR_TIMEZONE,
};

const severityLabelMap = {
    critical: "Критично",
    warning: "Попередження",
    info: "Інфо",
};

const eventKindLabelMap = {
    heartbeat: "Перевірка зв'язку",
    warning: "Попередження",
    alarm: "Тривога",
    critical: "Тривога",
    resolved: "Нормалізовано",
    reading: "Показник",
    config: "Налаштування",
};

const statusLabelMap = {
    critical: "Тривога",
    warning: "Попередження",
    info: "Норма",
    resolved: "Нормалізовано",
    inactive: "Вимкнений",
    offline: "Немає зв'язку",
};

const HIDDEN_SENSORS_STORAGE_KEY = "client_hidden_sensor_ids";

/** Local calendar date YYYY-MM-DD for history default (today). */
const localDateISO = (date = new Date()) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const todayDateISO = () => localDateISO(new Date());

const readHiddenSensorIds = () => {
    try {
        const raw = localStorage.getItem(HIDDEN_SENSORS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return new Set((Array.isArray(parsed) ? parsed : []).map((id) => Number(id)).filter(Boolean));
    } catch {
        return new Set();
    }
};

const writeHiddenSensorIds = (ids) => {
    localStorage.setItem(HIDDEN_SENSORS_STORAGE_KEY, JSON.stringify([...ids]));
};

const wifiPrimarySsidOf = (sensor) => String(sensor?.config?.wifi_ssid || "").trim();
const wifiConnectedSsidOf = (sensor) => String(sensor?.config?.wifi_ssid_connected || "").trim();
const wifiAltSsidOf = (sensor) => String(sensor?.config?.wifi_ssid_alt || "").trim();
const wifiDisplaySsidOf = (sensor) => (
    wifiConnectedSsidOf(sensor) || wifiPrimarySsidOf(sensor) || wifiAltSsidOf(sensor)
);

const wifiNetworksOf = (sensor, extraSsid = "") => {
    const seen = new Set();
    const items = [];
    [wifiPrimarySsidOf(sensor), wifiAltSsidOf(sensor), String(extraSsid || "").trim()].forEach((ssid) => {
        if (!ssid || seen.has(ssid)) return;
        seen.add(ssid);
        items.push(ssid);
    });
    return items;
};

const formatWifiSignal = (rssi) => {
    const n = Number(rssi);
    if (!Number.isFinite(n)) return "";
    let quality = "слабкий";
    if (n >= -55) quality = "відмінний";
    else if (n >= -67) quality = "добрий";
    else if (n >= -75) quality = "середній";
    return `${n} дБм, ${quality}`;
};

const formatClimateMetricsLine = (sensor) => {
    const parts = [];
    if (sensor?.last_temp_c != null && Number.isFinite(Number(sensor.last_temp_c))) {
        parts.push(`${Number(sensor.last_temp_c).toFixed(1)} °C`);
    }
    if (sensor?.last_humidity != null && Number.isFinite(Number(sensor.last_humidity))) {
        parts.push(`${Math.round(Number(sensor.last_humidity))} %RH`);
    }
    if (sensor?.last_eco2 != null && Number.isFinite(Number(sensor.last_eco2))) {
        parts.push(`eCO₂ ${Math.round(Number(sensor.last_eco2))}`);
    }
    if (sensor?.last_tvoc != null && Number.isFinite(Number(sensor.last_tvoc))) {
        parts.push(`TVOC ${Math.round(Number(sensor.last_tvoc))}`);
    }
    if (sensor?.last_aqi != null && Number.isFinite(Number(sensor.last_aqi))) {
        parts.push(`AQI ${Math.round(Number(sensor.last_aqi))}`);
    }
    return parts.join(" · ");
};

const formatBatteryLine = (sensor) => {
    const level = Number(sensor?.last_battery_level);
    const volts = Number(sensor?.last_battery_volts);
    const hasLevel = Number.isFinite(level) && level >= 0 && level <= 100;
    const hasVolts = Number.isFinite(volts) && volts > 0.1;
    if (hasLevel) {
        return hasVolts ? `Батарея ${Math.round(level)}% (${volts.toFixed(2)} В)` : `Батарея ${Math.round(level)}%`;
    }
    if (hasVolts) {
        return `Батарея ${volts.toFixed(2)} В`;
    }
    return "";
};

const SENSOR_OFFLINE_MIN_STALE_SEC = 1800;

const parseSensorTimestampMs = (value) => {
    if (!value) return 0;
    const text = String(value).trim();
    if (!text) return 0;
    const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text}Z`;
    const ms = new Date(withZone).getTime();
    return Number.isFinite(ms) ? ms : 0;
};

const resolveSensorDisplayStatus = (sensor, nowMs = Date.now()) => {
    if (!sensor?.is_active) {
        return { key: "inactive", label: "Вимкнений адміністратором" };
    }

    const lastSeenMs = parseSensorTimestampMs(sensor?.last_seen_at);
    const heartbeatSec = Number(deviceSettingsFromSensorConfig(sensor?.config, sensor).heartbeat_interval_sec)
        || (resolveSensorChannel(sensor) === "water" ? 1800 : 900);
    const staleAfterMs = Math.max(heartbeatSec * 2.5, SENSOR_OFFLINE_MIN_STALE_SEC) * 1000;
    if (!lastSeenMs || nowMs - lastSeenMs > staleAfterMs) {
        return { key: "offline", label: "Немає зв'язку" };
    }

    const statusKey = String(sensor.last_status || "info").toLowerCase();
    return {
        key: statusKey,
        label: statusLabelMap[statusKey] || sensor.last_status || "Норма",
    };
};

const defaultAccess = {
    page_enabled: false,
    can_manage_private: false,
    can_view_public: false,
    public_sensor_count: 0,
};

const formatDateTime = (value, timeZone = DEFAULT_SENSOR_TIMEZONE) => formatSensorDateTime(value, timeZone);

const sortSensorItems = (items, sortBy, sortDir) => {
    const sorted = [...items];
    sorted.sort((a, b) => {
        let valA = a?.[sortBy] || "";
        let valB = b?.[sortBy] || "";
        if (typeof valA === "boolean") {
            valA = valA ? 1 : 0;
            valB = valB ? 1 : 0;
        } else {
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
        }
        if (sortDir === "asc") {
            return valA > valB ? 1 : valA < valB ? -1 : 0;
        }
        return valA < valB ? 1 : valA > valB ? -1 : 0;
    });
    return sorted;
};

const ClientSensors = ({ access = defaultAccess }) => {
    const [sensors, setSensors] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedSensorId, setSelectedSensorId] = useState(null);
    const [highlightedEventId, setHighlightedEventId] = useState(null);
    const [editingSensorId, setEditingSensorId] = useState(null);
    const [editedData, setEditedData] = useState(emptyEditForm);
    const [originalData, setOriginalData] = useState(emptyEditForm);
    const [claimForm, setClaimForm] = useState(emptyClaimForm);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyDraft, setHistoryDraft] = useState(() => ({
        location: "",
        type: "",
        sensorId: null,
        from: todayDateISO(),
        to: todayDateISO(),
    }));
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [historyLocationFilter, setHistoryLocationFilter] = useState("");
    const [historySensorTypeFilter, setHistorySensorTypeFilter] = useState("");
    const [sortBy, setSortBy] = useState("display_name");
    const [sortDir, setSortDir] = useState("asc");
    const [eventDateFrom, setEventDateFrom] = useState(() => todayDateISO());
    const [eventDateTo, setEventDateTo] = useState(() => todayDateISO());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [expandedSensorId, setExpandedSensorId] = useState(null);
    const [isMobile, setIsMobile] = useState(
        Capacitor.isNativePlatform() || window.innerWidth < 768
    );
    const [bleSetupSensor, setBleSetupSensor] = useState(null);
    const [shareEmail, setShareEmail] = useState("");
    const [sensorShares, setSensorShares] = useState([]);
    const [sharesSensorId, setSharesSensorId] = useState(null);
    const [hiddenSensorIds, setHiddenSensorIds] = useState(() => readHiddenSensorIds());
    const [nowTick, setNowTick] = useState(() => Date.now());

    const selectedSensor = useMemo(
        () => sensors.find((item) => Number(item.id) === Number(selectedSensorId)) || null,
        [sensors, selectedSensorId]
    );

    const sensorTypes = useMemo(() => {
        const types = new Set();
        sensors.forEach((s) => { if (s.sensor_type) types.add(s.sensor_type); });
        return Array.from(types).sort();
    }, [sensors]);

    const locations = useMemo(() => {
        const map = new Map();
        sensors.forEach((s) => {
            if (s.location_id) map.set(String(s.location_id), s.location_name || "");
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [sensors]);

    const filteredSensors = useMemo(() => {
        const q = String(searchQuery || "").trim().toLowerCase();
        return sensors.filter((sensor) => {
            if (hiddenSensorIds.has(Number(sensor.id))) {
                return false;
            }

            const matchesStatus = statusFilter === "all" ||
                (statusFilter === "active" && sensor.is_active === true) ||
                (statusFilter === "inactive" && sensor.is_active !== true);

            const matchesType = !typeFilter || sensor.sensor_type === typeFilter;

            const haystack = [
                sensor.display_name,
                sensor.device_uid,
                sensor.sensor_type,
                sensor.location_name,
            ].map((v) => String(v || "").toLowerCase()).join(" ");
            const matchesSearch = !q || haystack.includes(q);

            return matchesStatus && matchesType && matchesSearch;
        });
    }, [sensors, statusFilter, typeFilter, hiddenSensorIds, searchQuery]);
    const sortedSensors = useMemo(() => sortSensorItems(filteredSensors, sortBy, sortDir), [filteredSensors, sortBy, sortDir]);

    const historyDraftSensors = useMemo(() => sensors.filter((sensor) => {
        if (historyDraft.location && String(sensor.location_id) !== String(historyDraft.location)) return false;
        if (historyDraft.type && sensor.sensor_type !== historyDraft.type) return false;
        return true;
    }), [sensors, historyDraft.location, historyDraft.type]);

    const loadSensors = useCallback(async () => {
        if (!access.page_enabled) {
            setSensors([]);
            return [];
        }

        const sensorData = await listClientSensors();
        setSensors(Array.isArray(sensorData) ? sensorData : []);
        return Array.isArray(sensorData) ? sensorData : [];
    }, [access.page_enabled]);

    const loadEvents = useCallback(async (sensorId, dateFrom = "", dateTo = "") => {
        if (!sensorId) {
            setEvents([]);
            return;
        }
        const response = await fetchClientSensorEvents(sensorId, {
            limit: 100,
            dateFrom,
            dateTo,
        });
        setEvents(Array.isArray(response?.items) ? response.items : []);
    }, []);

    const refreshAll = useCallback(async () => {
        if (!access.page_enabled) {
            setSensors([]);
            setEvents([]);
            setSelectedSensorId(null);
            return;
        }

        try {
            setLoading(true);
            setMessage("");
            const sensorData = await loadSensors();
            setSelectedSensorId((current) => {
                if (current && sensorData.some((item) => Number(item.id) === Number(current))) {
                    return current;
                }
                return sensorData[0]?.id || null;
            });
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [access.page_enabled, loadSensors]);

    useEffect(() => {
        refreshAll();
        const handleResize = () => {
            setIsMobile(Capacitor.isNativePlatform() || window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [refreshAll]);

    useEffect(() => {
        const timer = window.setInterval(() => setNowTick(Date.now()), 15000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!access.page_enabled) return undefined;
        const timer = window.setInterval(() => {
            loadSensors().catch(() => {});
            setNowTick(Date.now());
        }, 30000);
        return () => window.clearInterval(timer);
    }, [access.page_enabled, loadSensors]);

    useEffect(() => {
        // While history modal is open, loading is triggered by "Показати події".
        if (showHistoryModal) return undefined;

        let cancelled = false;

        const run = async () => {
            if (!access.page_enabled || !selectedSensorId) {
                setEvents([]);
                return;
            }
            try {
                const response = await fetchClientSensorEvents(selectedSensorId, {
                    limit: 100,
                    dateFrom: eventDateFrom,
                    dateTo: eventDateTo,
                });
                if (!cancelled) {
                    setEvents(Array.isArray(response?.items) ? response.items : []);
                }
            } catch (error) {
                if (!cancelled) {
                    setMessage(`❌ ${error.message}`);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [access.page_enabled, selectedSensorId, eventDateFrom, eventDateTo, showHistoryModal]);

    useEffect(() => {
        if (!showHistoryModal) return;
        setHistoryDraft({
            location: historyLocationFilter || "",
            type: historySensorTypeFilter || "",
            sensorId: selectedSensorId || null,
            from: eventDateFrom || todayDateISO(),
            to: eventDateTo || todayDateISO(),
        });
    }, [showHistoryModal, historyLocationFilter, historySensorTypeFilter, selectedSensorId, eventDateFrom, eventDateTo]);

    const openSensorHistory = useCallback((sensorId, eventId = null) => {
        const today = todayDateISO();
        const id = Number(sensorId) || null;
        setSelectedSensorId(id);
        setEventDateFrom(today);
        setEventDateTo(today);
        setHistoryLocationFilter("");
        setHistorySensorTypeFilter("");
        setHistoryDraft({
            location: "",
            type: "",
            sensorId: id,
            from: today,
            to: today,
        });
        setShowHistoryModal(true);
        if (Number.isFinite(Number(eventId)) && Number(eventId) > 0) {
            setHighlightedEventId(Number(eventId));
        }
        if (!id) return;
        setHistoryLoading(true);
        loadEvents(id, today, today)
            .catch((error) => setMessage(`❌ ${error.message}`))
            .finally(() => setHistoryLoading(false));
    }, [loadEvents]);

    const applyHistoryFilters = useCallback(async () => {
        const nextFrom = historyDraft.from || todayDateISO();
        const nextTo = historyDraft.to || todayDateISO();
        const nextSensorId = historyDraft.sensorId ? Number(historyDraft.sensorId) : null;

        setHistoryLocationFilter(historyDraft.location || "");
        setHistorySensorTypeFilter(historyDraft.type || "");
        setSelectedSensorId(nextSensorId);
        setEventDateFrom(nextFrom);
        setEventDateTo(nextTo);
        setHistoryLoading(true);
        setMessage("");
        try {
            if (!nextSensorId) {
                setEvents([]);
                return;
            }
            await loadEvents(nextSensorId, nextFrom, nextTo);
        } catch (error) {
            setMessage(`❌ ${error.message}`);
            setEvents([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [historyDraft, loadEvents]);

    useEffect(() => {
        if (!sensors.length) return;

        let pendingSensorId = null;
        let pendingEventId = null;
        try {
            pendingSensorId = parseInt(localStorage.getItem("pendingSensorOpenId_client"), 10);
            pendingEventId = parseInt(localStorage.getItem("pendingSensorEventOpenId_client"), 10);
        } catch (_) {
            pendingSensorId = null;
            pendingEventId = null;
        }

        const sensorExists = Number.isFinite(pendingSensorId)
            && sensors.some((item) => Number(item.id) === pendingSensorId);
        if (!sensorExists && !(Number.isFinite(pendingEventId) && pendingEventId > 0)) {
            return;
        }

        if (sensorExists) {
            openSensorHistory(pendingSensorId, pendingEventId);
        } else if (Number.isFinite(pendingEventId) && pendingEventId > 0) {
            setHighlightedEventId(pendingEventId);
            setShowHistoryModal(true);
        }

        try {
            localStorage.removeItem("pendingSensorOpenId_client");
            localStorage.removeItem("pendingSensorEventOpenId_client");
        } catch (_) {
            // ignore
        }
    }, [sensors, openSensorHistory]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir((current) => (current === "asc" ? "desc" : "asc"));
            return;
        }
        setSortBy(field);
        setSortDir("asc");
    };

    const startEdit = (sensor) => {
        if (!sensor) return;
        setEditingSensorId(sensor.id);
        setExpandedSensorId(sensor.id);
        const nextForm = {
            display_name: sensor.display_name || "",
            descriptions: sensor.descriptions || "",
            notify_client: Boolean(sensor.notify_client),
            is_active: Boolean(sensor.is_active),
            thresholds: thresholdsFromSensorConfig(sensor.config),
            timezone: timezoneFromSensorConfig(sensor.config),
            wifi_ssid: wifiPrimarySsidOf(sensor),
            wifi_ssid_alt: wifiAltSsidOf(sensor),
            wifi_pass_alt: "",
            wifi_alt_clear: false,
            wifi_primary_ssid: wifiPrimarySsidOf(sensor) || wifiAltSsidOf(sensor),
            ...deviceSettingsFromSensorConfig(sensor.config, sensor),
        };
        setEditedData(nextForm);
        setOriginalData(nextForm);
        if (sensor.is_private && sensor.is_owner !== false) {
            loadShares(sensor.id);
        }
    };

    const loadShares = async (sensorId) => {
        try {
            setSharesSensorId(sensorId);
            const items = await listClientSensorShares(sensorId);
            setSensorShares(Array.isArray(items) ? items : []);
        } catch (_) {
            setSensorShares([]);
        }
    };

    const handleCancelEdit = useCallback(() => {
        setEditedData(originalData);
        setEditingSensorId(null);
        setShareEmail("");
    }, [originalData]);

    useEffect(() => {
        if (!showAddModal && !showInfoModal && !editingSensorId) {
            return undefined;
        }
        return pushAndroidBackHandler(() => {
            if (showInfoModal) {
                setShowInfoModal(false);
                return true;
            }
            if (showAddModal) {
                setShowAddModal(false);
                return true;
            }
            if (editingSensorId) {
                handleCancelEdit();
                return true;
            }
            return false;
        });
    }, [showAddModal, showInfoModal, editingSensorId, handleCancelEdit]);

    const toggleSensorExpanded = (sensorId, isEditing) => {
        if (isEditing) return;
        setExpandedSensorId((prev) => (prev === sensorId ? null : sensorId));
        setSelectedSensorId(sensorId);
    };

    const isPrivateSensor = (sensor) => sensor?.is_private === true || sensor?.is_private === 1;
    const isOwnedPrivateSensor = (sensor) => isPrivateSensor(sensor) && sensor?.is_owner !== false && sensor?.access_role !== "shared";

    const handleClaim = async () => {
        if (!access.can_manage_private) return;
        try {
            setSaving(true);
            setMessage("");
            await claimClientSensor({
                ...claimForm,
                device_uid: claimForm.device_uid.trim().toUpperCase(),
                pairing_code: String(claimForm.pairing_code || "").trim().toUpperCase(),
                location_id: null,
            });
            setClaimForm(emptyClaimForm);
            setShowAddModal(false);
            await refreshAll();
            setMessage("✅ Сенсор успішно прив'язано");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (sensorId) => {
        try {
            setSaving(true);
            setMessage("");
            const currentSensor = sensors.find((item) => Number(item.id) === Number(sensorId));
            if (!currentSensor) {
                throw new Error("Сенсор не знайдено")
            }

            const channel = resolveSensorChannel(currentSensor);
            const payload = currentSensor.is_private
                ? (isOwnedPrivateSensor(currentSensor) ? {
                    display_name: editedData.display_name,
                    descriptions: editedData.descriptions,
                    notify_client: Boolean(editedData.notify_client),
                    is_active: Boolean(editedData.is_active),
                    config: mergeTimezoneIntoConfig(
                        mergeDeviceSettingsIntoConfig(
                            mergeThresholdsIntoConfig(currentSensor.config, editedData.thresholds, channel),
                            {
                                heartbeat_interval_sec: editedData.heartbeat_interval_sec,
                                buzzer_enabled: editedData.buzzer_enabled,
                            },
                            currentSensor
                        ),
                        editedData.timezone
                    ),
                } : {
                    notify_client: Boolean(editedData.notify_client),
                })
                : {
                    notify_client: Boolean(editedData.notify_client),
                };

            if (payload.config && isOwnedPrivateSensor(currentSensor)) {
                const ssidAlt = String(editedData.wifi_ssid_alt || "").trim();
                payload.config = {
                    ...payload.config,
                    wifi_ssid: String(editedData.wifi_ssid || wifiPrimarySsidOf(currentSensor) || "").trim(),
                    wifi_ssid_alt: ssidAlt,
                    wifi_pass_alt: String(editedData.wifi_pass_alt || ""),
                    wifi_alt_clear: Boolean(editedData.wifi_alt_clear) || !ssidAlt,
                    wifi_primary_ssid: String(editedData.wifi_primary_ssid || editedData.wifi_ssid || "").trim(),
                    wifi_connect_ssid: String(editedData.wifi_primary_ssid || editedData.wifi_ssid || "").trim(),
                };
            }

            await updateClientSensor(sensorId, payload);
            await refreshAll();
            await loadEvents(sensorId, eventDateFrom, eventDateTo);
            setEditingSensorId(null);
            setMessage("✅ Налаштування сенсора оновлено. Чип підхопить їх на наступному виході на зв'язок або через Bluetooth.");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleShareInvite = async (sensorId) => {
        try {
            setSaving(true);
            setMessage("");
            await createClientSensorShare(sensorId, { email: shareEmail.trim() });
            setShareEmail("");
            await loadShares(sensorId);
            setMessage("✅ Доступ до сенсора надано");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleShareRemove = async (sensorId, shareId) => {
        try {
            setSaving(true);
            setMessage("");
            await deleteClientSensorShare(sensorId, shareId);
            await loadShares(sensorId);
            setMessage("✅ Спільний доступ скасовано");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleHideSensor = (sensor) => {
        const id = Number(sensor?.id);
        if (!id) return;
        const next = new Set(hiddenSensorIds);
        next.add(id);
        writeHiddenSensorIds(next);
        setHiddenSensorIds(next);
        if (Number(selectedSensorId) === id) {
            setSelectedSensorId(null);
            setEvents([]);
        }
        setMessage("Сенсор приховано на цьому пристрої. Показати знову можна кнопкою «Показати приховані».");
    };

    const handleUnhideAllSensors = () => {
        writeHiddenSensorIds(new Set());
        setHiddenSensorIds(new Set());
        setMessage("Усі приховані сенсори знову видно на цьому пристрої.");
    };

    const handleNotifyToggle = async (sensor, enabled) => {
        const id = Number(sensor?.id);
        if (!id) return;
        try {
            setSaving(true);
            setMessage("");
            await updateClientSensor(id, { notify_client: Boolean(enabled) });
            await refreshAll();
            setMessage(enabled ? "✅ Сповіщення увімкнено" : "✅ Сповіщення вимкнено");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleBuzzerToggle = async (sensor, enabled) => {
        const id = Number(sensor?.id);
        if (!id || !isOwnedPrivateSensor(sensor)) return;
        try {
            setSaving(true);
            setMessage("");
            const nextConfig = mergeDeviceSettingsIntoConfig(
                sensor.config,
                { buzzer_enabled: Boolean(enabled) },
                sensor
            );
            await updateClientSensor(id, { config: nextConfig });
            await refreshAll();
            setMessage(
                enabled
                    ? "✅ Біпер увімкнено. Чип підхопить на наступному виході на зв'язок або через Bluetooth."
                    : "✅ Біпер вимкнено. Чип підхопить на наступному виході на зв'язок або через Bluetooth."
            );
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDetachSensor = async (sensor) => {
        const id = Number(sensor?.id);
        if (!id) return;

        const isShared = sensor?.access_role === "shared";
        const isOwned = isOwnedPrivateSensor(sensor);
        if (!isOwned && !isShared) {
            handleHideSensor(sensor);
            return;
        }

        const confirmText = isOwned
            ? "Відв'язати цей сенсор від акаунта? Його можна буде додати знову за кодом активації."
            : "Прибрати спільний сенсор зі свого списку?";
        if (!window.confirm(confirmText)) {
            return;
        }

        try {
            setSaving(true);
            setMessage("");
            await detachClientSensor(id);
            if (Number(selectedSensorId) === id) {
                setSelectedSensorId(null);
                setEvents([]);
            }
            await refreshAll();
            setMessage(isOwned ? "✅ Сенсор відв'язано" : "✅ Спільний доступ прибрано зі списку");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const canRemoveFromAccount = (sensor) => Boolean(
        isOwnedPrivateSensor(sensor) || sensor?.access_role === "shared"
    );

    const canHideOrRemoveSensor = (sensor) => {
        if (!sensor) return false;
        if (canRemoveFromAccount(sensor)) return true;
        const status = resolveSensorDisplayStatus(sensor, nowTick);
        return status.key === "inactive" || status.key === "offline";
    };

    const handleRebootSensor = async (sensor) => {
        if (!isOwnedPrivateSensor(sensor)) return;
        try {
            setSaving(true);
            setMessage("");
            const connectSsid = String(
                editedData.wifi_primary_ssid
                || wifiPrimarySsidOf(sensor)
                || wifiConnectedSsidOf(sensor)
                || ""
            ).trim();
            await updateClientSensor(sensor.id, {
                config: {
                    ...(sensor.config && typeof sensor.config === "object" ? sensor.config : {}),
                    reboot_requested: true,
                    wifi_connect_ssid: connectSsid,
                    wifi_primary_ssid: connectSsid || wifiPrimarySsidOf(sensor),
                },
            });
            setMessage("✅ Команду перезавантаження надіслано. Сенсор виконає її на наступному виході на зв'язок.");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleBleSetupComplete = async ({ notificationMode, wifiSsid, wifiSsidAlt } = {}) => {
        if (!bleSetupSensor?.id) return;
        try {
            const currentSensor = sensors.find((item) => Number(item.id) === Number(bleSetupSensor.id));
            if (!currentSensor?.is_private) return;

            const primary = String(wifiSsid || "").trim();
            const alt = String(wifiSsidAlt || "").trim();
            const nextConfig = {
                ...(currentSensor.config && typeof currentSensor.config === "object" ? currentSensor.config : {}),
                ble_provisioned_at: new Date().toISOString(),
                notification_mode: notificationMode,
                wifi_ssid: primary || wifiPrimarySsidOf(currentSensor),
                wifi_ssid_alt: alt,
                wifi_alt_clear: !alt,
                wifi_primary_ssid: primary || alt,
            };

            await updateClientSensor(bleSetupSensor.id, {
                config: nextConfig,
            });
            await refreshAll();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setBleSetupSensor(null);
        }
    };

    const canBleSetupSensor = (sensor) => Boolean(
        isOwnedPrivateSensor(sensor) && access.page_enabled
    );

    const renderThresholdPreview = (sensor) => {
        const channel = resolveSensorChannel(sensor || {});
        const th = thresholdsFromSensorConfig(sensor?.config);
        const settings = deviceSettingsFromSensorConfig(sensor?.config, sensor);
        const tz = timezoneFromSensorConfig(sensor?.config);
        const hbSec = Number(settings.heartbeat_interval_sec) || (channel === "water" ? 1800 : 900);
        const intervalLabel = channel === "water" ? "Інтервал перевірки" : "Інтервал зв'язку";
        const intervalText = hbSec < 60 ? `${hbSec} с` : `${hbSec} с (~${Math.round(hbSec / 60)} хв)`;
        const connected = wifiConnectedSsidOf(sensor);
        const signal = formatWifiSignal(sensor.last_signal_rssi);
        const lines = [];
        if (channel === "mq2" || channel === "both") {
            lines.push(`дим/газ ${th.mq2_warning}/${th.mq2_alarm}`);
        }
        if (channel === "mq135" || channel === "both") {
            lines.push(`повітря ${th.mq135_warning}/${th.mq135_alarm}`);
        }
        if (channel === "ens160") {
            lines.push(`eCO₂ ${th.eco2_warning}/${th.eco2_alarm} ppm`, `TVOC ${th.tvoc_warning}/${th.tvoc_alarm} ppb`);
        }
        if (channel === "water") {
            lines.push("протікання: електроди / DO");
        }
        return (
            <div className="client-sensors-thresholds-preview">
                <div>Часовий пояс: {tz}</div>
                <div>{intervalLabel}: {intervalText}</div>
                <div>Біпер: {settings.buzzer_enabled !== false ? "увімкнено" : "вимкнено"}</div>
                <div>Головний Wi-Fi: {wifiPrimarySsidOf(sensor) || "не задано"}</div>
                <div>Запасний Wi-Fi: {wifiAltSsidOf(sensor) || "не задано"}</div>
                {connected ? <div>Зараз підключено: {connected}{signal ? ` (${signal})` : ""}</div> : null}
                {lines.length ? <div>Пороги: {lines.join("; ")}</div> : null}
            </div>
        );
    };

    const renderThresholdEditor = (sensor) => {
        const channel = resolveSensorChannel(sensor || {});
        const showMq2 = channel === "mq2" || channel === "both";
        const showMq135 = channel === "mq135" || channel === "both";
        const showEns160 = channel === "ens160";
        const isWater = channel === "water";
        const note = isWater
            ? "Вода: deep sleep + перевірка за інтервалом (за замовч. 30 хв). Протікання будить одразу (EXT0). Мін. 5 хв."
            : showEns160
            ? "ENS160: eCO₂ (ppm) і TVOC (ppb). За замовчуванням eCO₂ 1000/1500, TVOC 250/500."
            : channel === "mq135"
                ? "Чутливість MQ-135 (ADC 0–4095). Попередження за замовчуванням від 1800."
                : channel === "mq2"
                    ? "Чутливість MQ-2 (ADC 0–4095). У кімнаті без диму часто ~2000–2200 — попередження від 2800."
                    : "Чутливість (ADC 0–4095). MQ-2 і MQ-135 — різні фізичні сенсори; показуються обидва пороги, якщо тип не визначено.";
        const intervalMin = isWater ? 300 : 15;
        const intervalDefault = isWater ? 1800 : DEFAULT_SENSOR_DEVICE_SETTINGS.heartbeat_interval_sec;
        const intervalLabel = isWater ? "Інтервал перевірки (сек)" : "Інтервал зв'язку (сек)";
        const wifiChoices = wifiNetworksOf(sensor, editedData.wifi_ssid_alt);
        const selectedWifi = String(editedData.wifi_primary_ssid || wifiPrimarySsidOf(sensor) || "").trim();
        return (
        <div className="client-sensors-thresholds">
            <div className="client-sensors-mode-note">{note}</div>
            <label>
                Часовий пояс подій
                <CompactSelect
                    ariaLabel="Часовий пояс подій"
                    value={editedData.timezone || DEFAULT_SENSOR_TIMEZONE}
                    options={SENSOR_TIMEZONE_OPTIONS.map((item) => ({
                        value: item.value,
                        label: item.label,
                    }))}
                    onChange={(next) => setEditedData((prev) => ({ ...prev, timezone: next }))}
                />
            </label>
            <div className="client-sensors-mode-note">
                Події зберігаються в UTC на сервері; тут лише як показувати дату/час у додатку.
            </div>
            <div className="client-sensors-form-grid">
                <label>
                    {intervalLabel}
                    <input
                        type="number"
                        min={intervalMin}
                        max={86400}
                        step={isWater ? 60 : 15}
                        value={editedData.heartbeat_interval_sec ?? intervalDefault}
                        onChange={(e) => setEditedData((prev) => ({
                            ...prev,
                            heartbeat_interval_sec: e.target.value,
                        }))}
                    />
                </label>
            </div>
            <div className="client-sensors-form-grid">
                <label className="client-sensors-form-span-2">
                    Запасний Wi-Fi
                    <input
                        type="text"
                        value={editedData.wifi_ssid_alt || ""}
                        placeholder="SSID"
                        onChange={(e) => setEditedData((prev) => ({
                            ...prev,
                            wifi_ssid_alt: e.target.value,
                            wifi_alt_clear: !String(e.target.value || "").trim(),
                        }))}
                    />
                </label>
                <label className="client-sensors-form-span-2">
                    Пароль запасного Wi-Fi
                    <input
                        type="password"
                        value={editedData.wifi_pass_alt || ""}
                        placeholder="Пароль"
                        disabled={!String(editedData.wifi_ssid_alt || "").trim()}
                        onChange={(e) => setEditedData((prev) => ({
                            ...prev,
                            wifi_pass_alt: e.target.value,
                        }))}
                    />
                </label>
            </div>
            {wifiChoices.length ? (
                <div className="client-sensors-notify-radios" role="radiogroup" aria-label="Мережа для підключення">
                    {wifiChoices.map((ssid) => {
                        const isConnected = wifiConnectedSsidOf(sensor) === ssid;
                        const signal = isConnected ? formatWifiSignal(sensor.last_signal_rssi) : "";
                        return (
                            <label key={ssid} className="client-sensors-check-inline">
                                <input
                                    type="radio"
                                    name={`wifi-primary-${sensor.id}`}
                                    checked={selectedWifi === ssid}
                                    onChange={() => setEditedData((prev) => ({
                                        ...prev,
                                        wifi_primary_ssid: ssid,
                                        wifi_ssid: prev.wifi_ssid || wifiPrimarySsidOf(sensor),
                                    }))}
                                />
                                <span>
                                    {ssid}
                                    {ssid === wifiPrimarySsidOf(sensor) ? " · головна" : " · запасна"}
                                    {isConnected ? " · зараз" : ""}
                                    {signal ? ` · ${signal}` : ""}
                                </span>
                            </label>
                        );
                    })}
                </div>
            ) : null}
            <button
                type="button"
                className="add-btn add-btn--secondary client-sensors-delete-in-edit"
                onClick={() => handleRebootSensor(sensor)}
                disabled={saving}
            >
                <Power size={14} />
                Перезавантажити сенсор
            </button>
            {isWater ? (
                <div className="client-sensors-mode-note">
                    Пороги ADC не потрібні: тривога при замиканні електродів водою.
                </div>
            ) : (
            <div className="client-sensors-form-grid">
                {showMq2 ? (
                    <>
                        <label>
                            Дим / газ попередження
                            <input
                                type="number"
                                min={0}
                                max={4095}
                                value={editedData.thresholds?.mq2_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq2_warning}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, mq2_warning: e.target.value },
                                }))}
                            />
                        </label>
                        <label>
                            Дим / газ тривога
                            <input
                                type="number"
                                min={0}
                                max={4095}
                                value={editedData.thresholds?.mq2_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq2_alarm}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, mq2_alarm: e.target.value },
                                }))}
                            />
                        </label>
                    </>
                ) : null}
                {showMq135 ? (
                    <>
                        <label>
                            Повітря попередження
                            <input
                                type="number"
                                min={0}
                                max={4095}
                                value={editedData.thresholds?.mq135_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq135_warning}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, mq135_warning: e.target.value },
                                }))}
                            />
                        </label>
                        <label>
                            Повітря тривога
                            <input
                                type="number"
                                min={0}
                                max={4095}
                                value={editedData.thresholds?.mq135_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq135_alarm}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, mq135_alarm: e.target.value },
                                }))}
                            />
                        </label>
                    </>
                ) : null}
                {showEns160 ? (
                    <>
                        <label>
                            eCO₂ попередження (ppm)
                            <input
                                type="number"
                                min={0}
                                max={60000}
                                value={editedData.thresholds?.eco2_warning ?? DEFAULT_SENSOR_THRESHOLDS.eco2_warning}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, eco2_warning: e.target.value },
                                }))}
                            />
                        </label>
                        <label>
                            eCO₂ тривога (ppm)
                            <input
                                type="number"
                                min={0}
                                max={60000}
                                value={editedData.thresholds?.eco2_alarm ?? DEFAULT_SENSOR_THRESHOLDS.eco2_alarm}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, eco2_alarm: e.target.value },
                                }))}
                            />
                        </label>
                        <label>
                            TVOC попередження (ppb)
                            <input
                                type="number"
                                min={0}
                                max={60000}
                                value={editedData.thresholds?.tvoc_warning ?? DEFAULT_SENSOR_THRESHOLDS.tvoc_warning}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, tvoc_warning: e.target.value },
                                }))}
                            />
                        </label>
                        <label>
                            TVOC тривога (ppb)
                            <input
                                type="number"
                                min={0}
                                max={60000}
                                value={editedData.thresholds?.tvoc_alarm ?? DEFAULT_SENSOR_THRESHOLDS.tvoc_alarm}
                                onChange={(e) => setEditedData((prev) => ({
                                    ...prev,
                                    thresholds: { ...prev.thresholds, tvoc_alarm: e.target.value },
                                }))}
                            />
                        </label>
                    </>
                ) : null}
            </div>
            )}
        </div>
        );
    };

    const renderShareEditor = (sensor) => {
        if (!isOwnedPrivateSensor(sensor)) return null;
        const shares = Number(sharesSensorId) === Number(sensor.id) ? sensorShares : [];
        return (
            <div className="client-sensors-share">
                <div className="client-sensors-mode-note">
                    Поділіться приватним сенсором з іншими мешканцями (email акаунта в додатку) — вони бачитимуть сповіщення.
                </div>
                <div className="client-sensors-form-grid">
                    <input
                        type="email"
                        placeholder="email@example.com"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                    />
                    <button
                        type="button"
                        className="add-btn"
                        disabled={saving || !shareEmail.trim()}
                        onClick={() => handleShareInvite(sensor.id)}
                    >
                        Надати доступ
                    </button>
                </div>
                {shares.length ? (
                    <ul className="client-sensors-share-list">
                        {shares.map((share) => (
                            <li key={share.id}>
                                <span>{share.shared_with_name || share.shared_with_email || share.shared_with_client_id}</span>
                                <button
                                    type="button"
                                    className="icon-button"
                                    title="Скасувати доступ"
                                    disabled={saving}
                                    onClick={() => handleShareRemove(sensor.id, share.id)}
                                >
                                    <X size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
        );
    };

    const renderBleSetupButton = (sensor, className = "add-btn client-sensors-ble-btn") => {
        if (!canBleSetupSensor(sensor)) return null;
        return (
            <button
                type="button"
                className={className}
                onClick={(event) => {
                    event.stopPropagation();
                    setBleSetupSensor(sensor);
                }}
                disabled={saving}
            >
                <Bluetooth size={14} /> Bluetooth
            </button>
        );
    };

    const renderMobileCardActions = (sensor, isEditing) => (
        <div
            className={`client-sensors-card-actions ${isEditing ? "is-editing" : "is-idle"}`}
            onClick={(event) => event.stopPropagation()}
        >
            {isEditing ? (
                <>
                    <button
                        type="button"
                        className="client-sensors-card-action is-primary-save"
                        onClick={() => handleSave(sensor.id)}
                        title="Зберегти"
                        aria-label="Зберегти"
                        disabled={saving}
                    >
                        <Save size={16} />
                    </button>
                    <button
                        type="button"
                        className="client-sensors-card-action is-cancel"
                        onClick={handleCancelEdit}
                        title="Скасувати"
                        aria-label="Скасувати"
                        disabled={saving}
                    >
                        <X size={16} />
                    </button>
                </>
            ) : (
                <>
                    <button
                        type="button"
                        className="client-sensors-card-action is-primary-edit"
                        onClick={() => startEdit(sensor)}
                        title={isPrivateSensor(sensor) ? "Редагувати" : "Редагувати сповіщення"}
                        aria-label={isPrivateSensor(sensor) ? "Редагувати" : "Редагувати сповіщення"}
                        disabled={saving}
                    >
                        <Edit size={16} />
                    </button>
                    {!isPrivateSensor(sensor) && canHideOrRemoveSensor(sensor) ? (
                        <button
                            type="button"
                            className="client-sensors-card-action is-cancel"
                            onClick={() => handleHideSensor(sensor)}
                            title="Приховати публічний сенсор лише на цьому пристрої"
                            aria-label="Приховати публічний сенсор на цьому пристрої"
                            disabled={saving}
                        >
                            <EyeOff size={16} />
                        </button>
                    ) : null}
                </>
            )}
        </div>
    );

    const renderToggleRow = ({ label, checked, disabled, onChange, ariaLabel }) => (
        <div className="client-sensors-toggle-row">
            <span className="client-sensors-toggle-row__label">{label}</span>
            <button
                type="button"
                className={`client-sensors-switch ${checked ? "is-on" : ""}`}
                role="switch"
                aria-checked={checked}
                aria-label={ariaLabel || label}
                disabled={disabled}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabled) return;
                    onChange(!checked);
                }}
            >
                <span className="client-sensors-switch__knob" />
            </button>
        </div>
    );

    const renderNotifyToggle = (sensor, { editing = false } = {}) => {
        const checked = editing ? editedData.notify_client === true : Boolean(sensor.notify_client);
        return renderToggleRow({
            label: "Сповіщення",
            checked,
            disabled: saving,
            onChange: (next) => {
                if (editing) {
                    setEditedData((prev) => ({ ...prev, notify_client: next }));
                } else {
                    handleNotifyToggle(sensor, next);
                }
            },
        });
    };

    const renderBuzzerToggle = (sensor, { editing = false } = {}) => {
        if (!isOwnedPrivateSensor(sensor)) return null;
        const enabled = editing
            ? editedData.buzzer_enabled !== false
            : deviceSettingsFromSensorConfig(sensor.config, sensor).buzzer_enabled !== false;
        return (
            <>
                {renderToggleRow({
                    label: "Біпер",
                    checked: enabled,
                    disabled: saving,
                    onChange: (next) => {
                        if (editing) {
                            setEditedData((prev) => ({ ...prev, buzzer_enabled: next }));
                        } else {
                            handleBuzzerToggle(sensor, next);
                        }
                    },
                })}
            </>
        );
    };

    const renderFlagEditor = (sensor) => (
        <div className="client-sensors-flags">
            {renderNotifyToggle(sensor, { editing: true })}
            {renderBuzzerToggle(sensor, { editing: true })}
            {sensor?.is_private && isOwnedPrivateSensor(sensor) ? (
                renderToggleRow({
                    label: "Активний",
                    checked: editedData.is_active === true,
                    disabled: saving,
                    onChange: (next) => setEditedData((prev) => ({ ...prev, is_active: next })),
                })
            ) : null}
            {canRemoveFromAccount(sensor) ? (
                <button
                    type="button"
                    className="add-btn add-btn--secondary client-sensors-delete-in-edit"
                    onClick={() => handleDetachSensor(sensor)}
                    disabled={saving}
                >
                    <Trash2 size={14} />
                    {isOwnedPrivateSensor(sensor) ? "Видалити сенсор" : "Прибрати зі списку"}
                </button>
            ) : null}
            {!sensor?.is_private ? <div className="client-sensors-mode-note">Ця настройка змінює тільки ваші власні push-сповіщення для цього публічного сенсора.</div> : null}
            {!sensor?.is_private ? <div className="client-sensors-mode-note">Біпер публічного сенсора керує лише оператор мережі.</div> : null}
            {sensor?.access_role === "shared" ? <div className="client-sensors-mode-note">Спільний доступ: можна лише керувати своїми сповіщеннями. Біпер керує власник сенсора.</div> : null}
        </div>
    );

    const renderFlagPreview = (sensor) => (
        <div className="client-sensors-flags client-sensors-flags-preview">
            {renderNotifyToggle(sensor, { editing: false })}
            {renderBuzzerToggle(sensor, { editing: false })}
            {!isOwnedPrivateSensor(sensor) ? (
                <div className="client-sensors-mode-note">
                    Біпер: {deviceSettingsFromSensorConfig(sensor.config, sensor).buzzer_enabled !== false ? "увімкнено" : "вимкнено"}
                    {!sensor?.is_private ? " (керує оператор мережі)" : sensor?.access_role === "shared" ? " (керує власник)" : ""}
                </div>
            ) : null}
            {sensor.is_private ? (
                sensor.access_role === "shared"
                    ? <span className="client-sensors-flag-badge">Спільний доступ</span>
                    : (sensor.is_active ? <span className="client-sensors-flag-badge is-active">Активний</span> : <span className="client-sensors-flag-badge is-muted">Вимкнений</span>)
            ) : <span className="client-sensors-flag-badge">Публічний</span>}
            {!sensor?.is_private ? (
                <div className="client-sensors-mode-note">
                    Кнопка з оком ховає публічний сенсор лише на цьому пристрої. Щоб повернути — натисніть «Показати приховані».
                </div>
            ) : null}
        </div>
    );

    const renderAddModal = () => {
        if (!showAddModal || !access.can_manage_private) return null;

        return (
            <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                <div className="modal-content client-sensors-modal" onClick={(e) => e.stopPropagation()}>
                    <h3>Додати сенсор</h3>
                    <div className="client-sensors-form-grid">
                        <input
                            type="text"
                            placeholder="Серійний номер"
                            value={claimForm.device_uid}
                            onChange={(e) => setClaimForm({ ...claimForm, device_uid: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Код активації"
                            value={claimForm.pairing_code}
                            onChange={(e) => setClaimForm({ ...claimForm, pairing_code: e.target.value })}
                        />
                        <div className="client-sensors-inline-note">
                            Додайте приватний сенсор за серійним номером і кодом активації від адміністратора. Локація для приватного сенсора не потрібна.
                        </div>
                        <input
                            className="client-sensors-form-span-2"
                            type="text"
                            placeholder="Назва сенсора"
                            value={claimForm.display_name}
                            onChange={(e) => setClaimForm({ ...claimForm, display_name: e.target.value })}
                        />
                        <textarea
                            className="client-sensors-form-span-2"
                            placeholder="Опис або примітка"
                            value={claimForm.descriptions}
                            onChange={(e) => setClaimForm({ ...claimForm, descriptions: e.target.value })}
                            rows={3}
                        />
                    </div>
                    <div className="modal-actions">
                        <button className="add-btn" onClick={handleClaim} disabled={saving}>
                            Додати
                        </button>
                        <button className="add-btn add-btn--secondary" onClick={() => setShowAddModal(false)} disabled={saving}>
                            Скасувати
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (!access.page_enabled) {
        return (
            <div className="client-sensors-page">
                <section className="sensor-panel sensor-disabled-panel">
                    <div className="sensor-panel-header">
                        <h3><ShieldAlert size={18} /> Сенсори недоступні</h3>
                    </div>
                    <div className="sensor-empty-state">
                        Модуль сенсорів ще не налаштований на сервері. Зверніться до підтримки.
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="client-sensors-page">

            <div className="config-header client-sensors-toolbar">
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        onClick={() => setShowAddModal(true)}
                        disabled={saving || !access.can_manage_private}
                        title="Додати сенсор"
                        aria-label="Додати сенсор"
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={refreshAll}
                        disabled={loading || saving}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={Boolean(typeFilter) || statusFilter !== "all"}
                        title="Фільтри сенсорів"
                        buttonTitle="Фільтри сенсорів"
                        onClear={() => {
                            setTypeFilter("");
                            setStatusFilter("all");
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Тип сенсора</span>
                            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                <option value="">Усі типи</option>
                                {sensorTypes.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">Усі</option>
                                <option value="active">Активні</option>
                                <option value="inactive">Неактивні</option>
                            </select>
                        </label>
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук сенсора"
                        title="Пошук сенсора"
                    />
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={() => openSensorHistory(selectedSensorId)}
                        disabled={saving}
                        title="Історія подій"
                        aria-label="Історія подій"
                    >
                        <History size={16} />
                    </button>
                    <button
                        className="add-btn add-btn--secondary toolbar-icon-btn client-sensors-info-btn"
                        type="button"
                        onClick={() => setShowInfoModal(true)}
                        disabled={saving}
                        title="Інфо"
                        aria-label="Інфо"
                    >
                        <Info size={16} />
                    </button>
                </div>
            </div>

            {message ? <div className="message-banner">{message}</div> : null}
            {hiddenSensorIds.size > 0 ? (
                <div className="message-banner client-sensors-hidden-banner" role="status">
                    <span>Приховано на цьому пристрої: {hiddenSensorIds.size}</span>
                    <button type="button" className="add-btn add-btn--secondary" onClick={handleUnhideAllSensors}>
                        Показати приховані
                    </button>
                </div>
            ) : null}
            {showInfoModal ? (
                <div className="modal-backdrop" onClick={() => setShowInfoModal(false)}>
                    <div className="modal-content client-sensors-modal client-sensors-info-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="client-sensors-ble-header">
                            <h3>Про сенсори</h3>
                            <button type="button" className="icon-button" onClick={() => setShowInfoModal(false)} title="Закрити">
                                <X className="icon-btn" />
                            </button>
                        </div>
                        <div className="client-sensors-info-modal__body">
                            Додайте приватний сенсор за кодом активації, увімкніть живлення і налаштуйте Wi-Fi через Bluetooth.
                            Після підключення до мережі тут з’являться також публічні сенсори вашої локації.
                            Для публічних сенсорів можна керувати власними push-сповіщеннями без редагування.
                            Кнопка з оком ховає публічний сенсор лише на цьому пристрої — повернути можна через «Показати приховані».
                            Видалення приватного сенсора доступне лише в режимі редагування.
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="add-btn" onClick={() => setShowInfoModal(false)}>
                                Зрозуміло
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {renderAddModal()}
            {bleSetupSensor ? (
                <SensorBleSetupModal
                    sensor={bleSetupSensor}
                    onClose={() => setBleSetupSensor(null)}
                    onComplete={handleBleSetupComplete}
                />
            ) : null}

            {isMobile ? (
                sortedSensors.length ? (
                    <div className="operator-accordion-list client-sensors-accordion-list">
                        {sortedSensors.map((sensor) => {
                            const isEditing = editingSensorId === sensor.id;
                            const isExpanded = expandedSensorId === sensor.id || isEditing;
                            const displayStatus = resolveSensorDisplayStatus(sensor, nowTick);
                            const canEditDetails = isEditing && isOwnedPrivateSensor(sensor);
                            const buzzerOn = deviceSettingsFromSensorConfig(sensor.config, sensor).buzzer_enabled !== false;
                            const wifiName = wifiDisplaySsidOf(sensor);
                            const wifiSignal = formatWifiSignal(sensor.last_signal_rssi);
                            return (
                                <div
                                    key={sensor.id}
                                    className="operator-accordion-item config-card client-sensors-accordion-item"
                                >
                                    <div
                                        className="operator-accordion-head client-sensors-accordion-head"
                                        onClick={() => {
                                            if (!isEditing) openSensorHistory(sensor.id);
                                        }}
                                    >
                                        <div className="operator-accordion-head-main">
                                            <button
                                                type="button"
                                                className="client-sensors-title-link"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleSensorExpanded(sensor.id, isEditing);
                                                }}
                                                aria-expanded={isExpanded}
                                            >
                                                {sensor.display_name || sensor.device_uid}
                                            </button>
                                            <div className="client-sensors-card-meta client-sensors-card-meta-status">
                                                <span className={`sensor-status-badge ${displayStatus.key}`}>{displayStatus.label}</span>
                                                <span>{sensor.sensor_type || "Без типу"}</span>
                                                <span>{isPrivateSensor(sensor) ? (sensor.access_role === "shared" ? "Спільний" : "Приватний") : "Публічний"}</span>
                                            </div>
                                            <div className="client-sensors-card-meta client-sensors-card-flags-row">
                                                <span>{sensor.notify_client ? "Сповіщення" : "Сповіщення вимк."}</span>
                                                <span>{buzzerOn ? "Біпер" : "Біпер вимк."}</span>
                                                <span className="client-sensors-card-wifi">{wifiName ? `Wi-Fi ${wifiName}${wifiSignal ? ` (${wifiSignal})` : ""}` : "Wi-Fi —"}</span>
                                            </div>
                                            {(formatClimateMetricsLine(sensor) || formatBatteryLine(sensor)) ? (
                                                <div className="client-sensors-card-meta client-sensors-card-metrics-row">
                                                    {formatClimateMetricsLine(sensor) ? (
                                                        <span>{formatClimateMetricsLine(sensor)}</span>
                                                    ) : null}
                                                    {formatBatteryLine(sensor) ? (
                                                        <span>{formatBatteryLine(sensor)}</span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>

                                    {renderMobileCardActions(sensor, isEditing)}
                                    {isEditing ? (
                                        <EditFloatingActions
                                            onSave={() => handleSave(sensor.id)}
                                            onCancel={handleCancelEdit}
                                            saveDisabled={saving}
                                            cancelDisabled={false}
                                        />
                                    ) : null}

                                    {isEditing && canBleSetupSensor(sensor) ? (
                                        <div className="client-sensors-card-ble-row">
                                            {renderBleSetupButton(sensor)}
                                        </div>
                                    ) : null}

                                    {isExpanded ? (
                                        <div className="operator-accordion-body client-sensors-accordion-body">
                                            <div className="operator-detail-grid">
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Серійний номер</span>
                                                    <div className="operator-detail-value">{sensor.device_uid}</div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Назва</span>
                                                    <div className="operator-detail-value">
                                                        {canEditDetails ? (
                                                            <input value={editedData.display_name} onChange={(e) => setEditedData({ ...editedData, display_name: e.target.value })} />
                                                        ) : sensor.display_name}
                                                    </div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Локація</span>
                                                    <div className="operator-detail-value">{sensor.location_name || "-"}</div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Останній зв&apos;язок</span>
                                                    <div className="operator-detail-value">{formatDateTime(sensor.last_seen_at, timezoneFromSensorConfig(sensor.config))}</div>
                                                </div>
                                                {formatClimateMetricsLine(sensor) ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Клімат / повітря</span>
                                                        <div className="operator-detail-value">{formatClimateMetricsLine(sensor)}</div>
                                                    </div>
                                                ) : null}
                                                {formatBatteryLine(sensor) ? (
                                                    <div className="operator-detail-block">
                                                        <span className="operator-detail-label">Батарея</span>
                                                        <div className="operator-detail-value">{formatBatteryLine(sensor)}</div>
                                                    </div>
                                                ) : null}
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Опис</span>
                                                    <div className="operator-detail-value">
                                                        {canEditDetails ? (
                                                            <textarea value={editedData.descriptions} onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })} rows={3} />
                                                        ) : (sensor.descriptions || "-")}
                                                    </div>
                                                </div>
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">{sensor.is_private ? "Налаштування" : "Мої сповіщення"}</span>
                                                    <div className="operator-detail-value client-sensors-card-flags">
                                                        {isEditing ? renderFlagEditor(sensor) : renderFlagPreview(sensor)}
                                                    </div>
                                                </div>
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Чутливість / інтервал зв&apos;язку</span>
                                                    <div className="operator-detail-value">
                                                        {canEditDetails ? renderThresholdEditor(sensor) : renderThresholdPreview(sensor)}
                                                    </div>
                                                </div>
                                                {isEditing && isOwnedPrivateSensor(sensor) ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Спільний доступ</span>
                                                        <div className="operator-detail-value">{renderShareEditor(sensor)}</div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="sensor-empty-state">
                        <div>Сенсори ще не додані.</div>
                        {access.can_manage_private ? (
                            <button
                                type="button"
                                className="add-btn"
                                style={{ marginTop: 12 }}
                                onClick={() => setShowAddModal(true)}
                                disabled={saving}
                            >
                                <Plus size={16} /> Додати сенсор
                            </button>
                        ) : null}
                    </div>
                )
            ) : sortedSensors.length ? (
                <div className="table-responsive">
                    <table className="client-sensors-table">
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("device_uid")}>Серійний номер {sortBy === "device_uid" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("display_name")}>Назва {sortBy === "display_name" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("sensor_type")}>Тип {sortBy === "sensor_type" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Режим</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_name")}>Локація {sortBy === "location_name" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Опис</th>
                                <th>Прапорці</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("last_seen_at")}>Останній зв'язок {sortBy === "last_seen_at" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Показання</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("last_status")}>Статус {sortBy === "last_status" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSensors.map((sensor) => {
                                const isEditing = editingSensorId === sensor.id;
                                const canEditDetails = isEditing && isOwnedPrivateSensor(sensor);
                                const displayStatus = resolveSensorDisplayStatus(sensor, nowTick);
                                return (
                                    <tr
                                        key={sensor.id}
                                        onClick={() => {
                                            if (!isEditing) openSensorHistory(sensor.id);
                                        }}
                                    >
                                        <td>{sensor.device_uid}</td>
                                        <td>
                                            {canEditDetails ? (
                                                <input value={editedData.display_name} onChange={(e) => setEditedData({ ...editedData, display_name: e.target.value })} />
                                            ) : sensor.display_name}
                                        </td>
                                        <td>{sensor.sensor_type || "-"}</td>
                                        <td>{sensor.is_private ? (sensor.access_role === "shared" ? "Спільний" : "Приватний") : "Публічний"}</td>
                                        <td>{sensor.location_name || "-"}</td>
                                        <td className="client-sensors-cell-text">
                                            {canEditDetails ? (
                                                <textarea value={editedData.descriptions} onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })} rows={3} />
                                            ) : <span>{sensor.descriptions || "-"}</span>}
                                        </td>
                                        <td className="client-sensors-cell-flags" onClick={(e) => e.stopPropagation()}>
                                            {isEditing ? (
                                                <>
                                                    {renderFlagEditor(sensor)}
                                                    {isOwnedPrivateSensor(sensor) ? (
                                                        <>
                                                            {renderThresholdEditor(sensor)}
                                                            {renderShareEditor(sensor)}
                                                        </>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <>
                                                    {renderFlagPreview(sensor)}
                                                    {renderThresholdPreview(sensor)}
                                                </>
                                            )}
                                        </td>
                                        <td>{formatDateTime(sensor.last_seen_at, timezoneFromSensorConfig(sensor.config))}</td>
                                        <td>
                                            <div>{formatClimateMetricsLine(sensor) || "—"}</div>
                                            {formatBatteryLine(sensor) ? <div>{formatBatteryLine(sensor)}</div> : null}
                                        </td>
                                        <td><span className={`sensor-status-badge ${displayStatus.key}`}>{displayStatus.label}</span></td>
                                        <td className="actions" onClick={(e) => e.stopPropagation()}>
                                            {isEditing ? (
                                                <>
                                                    {renderBleSetupButton(sensor, "add-btn client-sensors-ble-btn client-sensors-ble-btn--compact")}
                                                    <button className="icon-button" onClick={() => handleSave(sensor.id)} title="Зберегти" disabled={saving}>
                                                        <Save className="icon-btn" />
                                                    </button>
                                                    <button className="icon-button" onClick={handleCancelEdit} title="Скасувати" disabled={saving}>
                                                        <X className="icon-btn" />
                                                    </button>
                                                    {canRemoveFromAccount(sensor) ? (
                                                        <button className="icon-button" onClick={() => handleDetachSensor(sensor)} title="Видалити зі списку" disabled={saving}>
                                                            <Trash2 className="icon-btn" />
                                                        </button>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <>
                                                    <button className="icon-button" onClick={() => startEdit(sensor)} title={sensor.is_private ? "Редагувати" : "Редагувати сповіщення"} disabled={saving}>
                                                        <Edit className="icon-btn" />
                                                    </button>
                                                    {!isPrivateSensor(sensor) && canHideOrRemoveSensor(sensor) ? (
                                                        <button
                                                            className="icon-button"
                                                            onClick={() => handleHideSensor(sensor)}
                                                            title="Приховати публічний сенсор лише на цьому пристрої"
                                                            disabled={saving}
                                                        >
                                                            <EyeOff className="icon-btn" />
                                                        </button>
                                                    ) : null}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="sensor-empty-state">
                    <div>Сенсори ще не додані.</div>
                    {access.can_manage_private ? (
                        <button
                            type="button"
                            className="add-btn"
                            style={{ marginTop: 12 }}
                            onClick={() => setShowAddModal(true)}
                            disabled={saving}
                        >
                            <Plus size={16} /> Додати сенсор
                        </button>
                    ) : null}
                </div>
            )}

            {showHistoryModal ? (
                <div className="modal-backdrop" onClick={() => setShowHistoryModal(false)}>
                    <div
                        className="modal-content client-sensors-modal client-sensors-history-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="client-sensors-ble-header">
                            <h3><ShieldAlert size={18} /> Історія подій</h3>
                            <button type="button" className="icon-button" onClick={() => setShowHistoryModal(false)} title="Закрити">
                                <X className="icon-btn" />
                            </button>
                        </div>

                        <div className="client-sensors-history-filter-row">
                            <CompactSelect
                                ariaLabel="Локація"
                                value={historyDraft.location}
                                options={[
                                    { value: "", label: "Усі локації" },
                                    ...locations.map((loc) => ({
                                        value: String(loc.id),
                                        label: loc.name || "Без назви",
                                    })),
                                ]}
                                onChange={(next) => setHistoryDraft((prev) => ({
                                    ...prev,
                                    location: next,
                                    sensorId: null,
                                }))}
                            />
                            <CompactSelect
                                ariaLabel="Тип сенсора"
                                value={historyDraft.type}
                                options={[
                                    { value: "", label: "Усі типи сенсорів" },
                                    ...sensorTypes.map((t) => ({ value: t, label: t })),
                                ]}
                                onChange={(next) => setHistoryDraft((prev) => ({ ...prev, type: next }))}
                            />
                            <div className="client-sensors-history-selector">
                                <CompactSelect
                                    ariaLabel="Сенсор"
                                    value={historyDraft.sensorId || ""}
                                    options={[
                                        { value: "", label: "Оберіть сенсор" },
                                        ...historyDraftSensors.map((sensor) => ({
                                            value: String(sensor.id),
                                            label: `${sensor.display_name} • ${sensor.device_uid}`,
                                        })),
                                    ]}
                                    onChange={(next) => setHistoryDraft((prev) => ({
                                        ...prev,
                                        sensorId: next ? Number(next) : null,
                                    }))}
                                />
                            </div>
                        </div>

                        <div className="archive-date-filter client-sensors-date-filter">
                            <div className="archive-date-field client-sensors-date-field">
                                <label className="client-sensors-date-label" htmlFor="client-sensor-date-from">Від</label>
                                <input
                                    id="client-sensor-date-from"
                                    type="date"
                                    lang="uk"
                                    value={historyDraft.from}
                                    onChange={(e) => setHistoryDraft((prev) => ({
                                        ...prev,
                                        from: e.target.value || todayDateISO(),
                                    }))}
                                />
                            </div>
                            <span className="client-sensors-date-separator" aria-hidden="true">—</span>
                            <div className="archive-date-field client-sensors-date-field">
                                <label className="client-sensors-date-label" htmlFor="client-sensor-date-to">До</label>
                                <input
                                    id="client-sensor-date-to"
                                    type="date"
                                    lang="uk"
                                    value={historyDraft.to}
                                    onChange={(e) => setHistoryDraft((prev) => ({
                                        ...prev,
                                        to: e.target.value || todayDateISO(),
                                    }))}
                                />
                            </div>
                            <button
                                type="button"
                                className="clear-filter"
                                onClick={() => {
                                    const today = todayDateISO();
                                    setHistoryDraft((prev) => ({ ...prev, from: today, to: today }));
                                }}
                            >
                                Сьогодні
                            </button>
                        </div>

                        <div className="client-sensors-history-actions">
                            <button
                                type="button"
                                className="add-btn"
                                onClick={applyHistoryFilters}
                                disabled={historyLoading || !historyDraft.sensorId}
                            >
                                {historyLoading ? "Завантаження..." : "Показати події"}
                            </button>
                        </div>

                        {!historyDraft.sensorId ? (
                            <div className="sensor-empty-state">Оберіть сенсор і натисніть «Показати події».</div>
                        ) : historyLoading ? (
                            <div className="sensor-empty-state">Завантаження подій...</div>
                        ) : !events.length ? (
                            <div className="sensor-empty-state">Подій для цього сенсора ще немає. Змініть фільтри й натисніть «Показати події».</div>
                        ) : isMobile ? (
                            <div className="sensor-events-list">
                                {events.map((item) => {
                                    const severityKey = String(item.severity || "info").toLowerCase();
                                    return (
                                        <div
                                            key={item.id}
                                            className={`sensor-event-card ${severityKey} ${Number(highlightedEventId) === Number(item.id) ? "highlighted" : ""}`}
                                        >
                                            <div className="sensor-event-header">
                                                <strong>{item.title || "Подія сенсора"}</strong>
                                                <span className={`sensor-status-badge ${severityKey}`}>
                                                    {severityLabelMap[severityKey] || item.severity}
                                                </span>
                                            </div>
                                            <div className="sensor-meta-row">{formatDateTime(item.created_at, timezoneFromSensorConfig(selectedSensor?.config))} • {eventKindLabelMap[String(item.event_kind || "").toLowerCase()] || item.event_kind}</div>
                                            {item.body ? <p className="sensor-event-body">{item.body}</p> : null}
                                            <div className="sensor-event-metrics">
                                                <span>Дим/газ: {item.reading_smoke ?? "-"}</span>
                                                <span>Повітря: {item.reading_air ?? "-"}</span>
                                                <span>Батарея: {item.battery_level ?? "-"}</span>
                                                <span>Сигнал Wi‑Fi (RSSI): {item.signal_rssi ?? "-"}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="table-responsive client-sensors-history-table-wrap">
                                <table className="client-sensors-history-table">
                                    <thead>
                                        <tr>
                                            <th>Дата</th>
                                            <th>Подія</th>
                                            <th>Рівень</th>
                                            <th>Заголовок</th>
                                            <th>Опис</th>
                                            <th>Дим/газ</th>
                                            <th>Повітря</th>
                                            <th>Батарея</th>
                                            <th>RSSI (дБм)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {events.map((item) => {
                                            const severityKey = String(item.severity || "info").toLowerCase();
                                            return (
                                                <tr key={item.id} className={Number(highlightedEventId) === Number(item.id) ? "client-sensors-history-row-selected" : ""}>
                                                    <td>{formatDateTime(item.created_at, timezoneFromSensorConfig(selectedSensor?.config))}</td>
                                                    <td>{eventKindLabelMap[String(item.event_kind || "").toLowerCase()] || item.event_kind || "-"}</td>
                                                    <td>
                                                        <span className={`sensor-status-badge ${severityKey}`}>
                                                            {severityLabelMap[severityKey] || item.severity}
                                                        </span>
                                                    </td>
                                                    <td>{item.title || "Подія сенсора"}</td>
                                                    <td className="client-sensors-history-body">{item.body || "-"}</td>
                                                    <td>{item.reading_smoke ?? "-"}</td>
                                                    <td>{item.reading_air ?? "-"}</td>
                                                    <td>{item.battery_level ?? "-"}</td>
                                                    <td>{item.signal_rssi ?? "-"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientSensors;